<template>
  <div :class="containerClasses" ref="containerRef" v-bind="containerBindings">
    <fallback-screen-handler>
      <branding v-if="branded" :brand="activeTheme" />

      <result-header :header="statisticsStore.resultHeader" :tabs="widgetTabs" :disable-tabs="!isMultipleTabsWidget" @tab-changed="handleTabChange" :width="widgetWidth" />

      <component v-if="!unavailableMessage" :is="widgetContent.component" v-bind="widgetContent.props" />

      <message v-else :message="unavailableMessage" class="szw-mx-auto szw-mt-20" />
    </fallback-screen-handler>
  </div>
</template>

<script setup>
import { ref, computed, provide, watch, onBeforeUnmount, onBeforeMount } from 'vue'
import { loc } from '@/__core__/localization/localization'
import useFetch from '@/__core__/composables/useFetch'
import { useDataRefresh } from '@/__core__/data-refresh/useDataRefresh'
import { useWidgetContext } from '@/__v1.5__/composables/useWidgetContext'
import { useStatisticsStore } from '@/widgets/statistics/Statistics.store'
import ResultHeader from '@/__v1.5__/components/result-headers/ResultHeader.vue'
import KeyMoments from '@/__v1.5__/composits/key-moments/KeyMoments.vue'
import GameStats from '@/__v1.5__/composits/game-stats/GameStats.vue'
import FallbackScreenHandler from '@/__core__/components/fallbackScreens/FallbackScreenHandler.vue'
import Branding from '@/__v1.5__/components/branding/Branding.vue'
import Message from '@/__v1.5__/components/message/Message.vue'
import '@/__core__/style/style.scss'

const widgetId = 'statistics'

const props = defineProps({
  languageIdentifier: {
    type: String,
    default: () => 'eng'
  },
  eventId: {
    type: String,
    default: 'sr:sport_event:51273293'
  },
  sportType: {
    type: String,
    default: () => 'football'
  },
  region: {
    type: String,
    default: () => ''
  },
  theme: {
    type: String,
    default: 'light'
  },
  size: {
    type: Number,
    default: 640
  },
  tabs: Array,
  themeConfig: {
    type: Object,
    default: () => null
  }
})

const statisticsStore = useStatisticsStore()
const { startRefresh } = useDataRefresh(60 * 1000, widgetId)

/** @type {Ref<'key-moments' | 'game-stats'>} */
const activeWidgetId = ref('key-moments')
const widgetTabs = ref([])
const containerRef = ref(null)

const { activeTheme, widgetWidth, branded, containerClasses, reorderedTabs, langId } = useWidgetContext({ ...props, containerRef, widgetId })

const { status } = useFetch(async () => {
  const payload = {
    region: props.region,
    eventId: props.eventId,
    sportType: props.sportType,
    languageIdentifier: langId
  }

  await statisticsStore.fetchStatisticsData(payload)

  const refreshFn = async () => statisticsStore.scheduleStatisticsDataRefresh(payload)
  const shouldCallApi = () => statisticsStore.getShouldCallApi()
  const shouldStopRefresh = () => statisticsStore.getShouldStopRefresh

  startRefresh(refreshFn, shouldCallApi, shouldStopRefresh)
  setupTabs(props.tabs)
})

const containerBindings = computed(() => ({
  'e2e-id': isMultipleTabsWidget.value ? 'szw-statistics-widget' : `szw-statistics-${props.tabs?.[0]?.id}-widget`, // legacy support for e2e tests; should be match-summary
  'theme-id': activeTheme.value,
  'lang-id': langId,
  'event-id': props.eventId,
  'drf-id': widgetId
}))

const widgetContent = computed(() => {
  if (isGameStats.value) {
    return {
      component: GameStats,
      props: {
        stats: stats.value
      }
    }
  } else {
    return {
      component: KeyMoments,
      props: {
        events: events.value
      }
    }
  }
})

const COVERAGE_LABELS_LOOKUP = {
  updatable: 'sm_info_liveticker_empty',
  limited: 'sm_limited_coverage',
  future: 'sm_data_unavailable_screen_refresh_automatically'
}

const isGameStats = computed(() => activeWidgetId.value === 'game-stats')
const unavailableMessage = computed(() => {
  if (status.value === 'loading') {
    return ''
  }

  const coverageCategory = statisticsStore.getCoverageCategory(activeWidgetId.value)
  return loc(COVERAGE_LABELS_LOOKUP[coverageCategory] ?? '', langId)
})

// *** KEY MOMENTS ***
const events = computed(() =>
  statisticsStore.timeline?.tickerData?.reduce((accumulatedEvents, event, index) => {
    const uuid = `event-${index + 1}`

    let cardType = 'caption'
    let title = event.kind ?? ''
    let team = { id: '', logo: '', name: '', shortName: '' }

    if (event.action !== 'period-info') {
      title = event.label
      team = statisticsStore.adjustedTeamsLookup[event.teamId]
      cardType = 'event'
    }

    accumulatedEvents.push({ ...event, uuid, title, team, cardType })

    return accumulatedEvents
  }, [])
)

// *** GAME STATS ***
const stats = computed(() =>
  status.value === 'success'
    ? {
        home: statisticsStore.getHomeStats?.teamStatistics,
        away: statisticsStore.getAwayStats?.teamStatistics
      }
    : {}
)

// *** TABS ***
const TABS_ID_LOOKUP = {
  moment: 'key-moments',
  stat: 'game-stats'
}

const isMultipleTabsWidget = computed(() => widgetTabs.value.length > 1)

function setupTabs(tabsTemplate) {
  let tabs = []

  if (tabsTemplate && tabsTemplate.length) {
    tabs = tabsTemplate.map(tab => ({ ...tab, id: TABS_ID_LOOKUP[tab.id], title: loc(tab.labelKey, langId) }))
  } else {
    tabs = [
      { title: loc('statistics_key_moments', langId), id: 'key-moments' },
      { title: loc('statistics_game_stats', langId), id: 'game-stats' }
    ]
  }

  widgetTabs.value = tabs
  setActiveWidgetId()
}

function setActiveWidgetId(newId) {
  activeWidgetId.value = newId || widgetTabs.value[0]?.id
}

function handleTabChange(newTab) {
  activeWidgetId.value = newTab.id
}

function setupFallbackTabs() {
  setActiveWidgetId(TABS_ID_LOOKUP[props.tabs?.[0]?.id])

  if (props.tabs?.length <= 1) {
    widgetTabs.value = []
  } else {
    widgetTabs.value = [
      { title: '', id: 'mock-1' },
      { title: '', id: 'mock-2' }
    ]
  }
}

const unwatchReorderedTabs = watch(reorderedTabs, setupTabs)

onBeforeMount(setupFallbackTabs)
onBeforeUnmount(unwatchReorderedTabs)

provide('status', status)
</script>
